import React from 'react';
import {
  Table as MuiTable, TableBody, TableCell, TableHead, TableRow,
  Box
} from '@material-ui/core';

import { Field, Table as TableType, TableRecord } from 'src/types';
import CRUDForm from 'src/components/formik/CRUDForm';
import { FIELD_DETAILS_BY_FIELD_TYPE } from 'src/views/constants/fieldConstants';
import { WIDGET_DETAILS_BY_WIDGET_TYPE } from 'src/views/constants/widgetConstants';
import Table from 'src/components/Table';
import useGetRequest from 'src/hooks/useGetRequest';
import Card from 'src/components/Card';
import Button from 'src/components/Button';
import { Formik, useFormikContext } from 'formik';


interface Props {
  engagementId: string,
  workflowId: string,
}

const FormikSaveButton = ({ children }: { children: React.ReactNode }) => {
  const formik = useFormikContext();

  return (
    <Button look="primary" onClick={() => formik.submitForm()}>
      {children}
    </Button>
  );
};

function Record({
  engagementId,
  workflowId,
}: Props) {
  const [editMode, setEditMode] = React.useState(false);
  const {
    result: table,
    httpRequest: refreshTable,
    isLoading: tableIsLoading
  }: {
    result: TableType,
    isLoading: boolean,
    httpRequest: () => void
  } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/table/`,
    initialResult: [],
  });
  const {
    result: record,
    httpRequest: refreshRecord,
    isLoading: recordIsLoading
  }: {
    result: TableRecord,
    isLoading: boolean,
    httpRequest: () => void
  } = useGetRequest({
    url: `/automatic/engagements/${engagementId}/record/`,
    initialResult: [],
  });

  if (tableIsLoading || recordIsLoading) return null;

  const columns = [
    {
      title: 'Field',
      cellContent: (field: Field) => field.name,
    },
    {
      title: '',
      cellContent: (field: Field) => {
        const Component = FIELD_DETAILS_BY_FIELD_TYPE[field.type].columnRenderer;

        return <Component value={record.valueByFieldId[field.id]} />;
      },
    },
  ];

  const editButton = (
    <Button look="primary" onClick={() => setEditMode(true)}>
      Edit
    </Button>
  );

  if (editMode) {
    const editColumns = [
      {
        title: 'Field',
        cellContent: (field: Field) => field.name,
      },
      {
        title: '',
        cellContent: (field: Field) => WIDGET_DETAILS_BY_WIDGET_TYPE[FIELD_DETAILS_BY_FIELD_TYPE[field.type].widgetType].renderer({
          label: field.name,
          name: field.id.toString(),
          options: field.options
        }),
      },
    ];

    return (
      <CRUDForm
        url={`/automatic/engagements/${engagementId}/tables/${table.id}/records/`}
        objectId={record && record.id}
        initialValuesOverride={record.valueByFieldId}
        render={(crudProps: any) => (
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            initialValues={crudProps.result}
            onSubmit={(values) => {
              const afterSubmit = () => {
                refreshTable();
                refreshRecord();
                setEditMode(false);
              };
              crudProps.onSubmit(values, afterSubmit);
            }}
          >
            <Card
              title="Data"
              p={0}
              action={(
                <Box display="flex">
                  <Box mr={1}>
                    <Button onClick={() => setEditMode(false)}>
                      Cancel
                    </Button>
                  </Box>
                  <FormikSaveButton>
                    Save
                  </FormikSaveButton>
                </Box>
              )}
            >
              <Table
                columns={editColumns}
                data={table.fields}
              />
            </Card>
          </Formik>
        )}
      />
    );
  }

  return (
    <Card title="Data" p={0} action={editButton}>
      <Table
        columns={columns}
        data={table.fields}
      />
    </Card>
  );
}

export default Record;
